import { UISpecCategory } from '@common/enums';
import { useAirflowGemList, useGemList } from '@common/gems/gem-hooks';
import { ComponentList as ComponentListType, GemRecord, QueryKeys } from '@common/gems/types';
import { SpecLanguage } from '@common/types';
import { TrashCIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { ColumnType, Dialog, Stack, TableActions, TableIconButton, Text, ToggleGroups, Tooltip } from '@prophecy/ui-v3';
import { StackItem } from '@prophecy/ui-v3/Layout';
import { toast } from '@prophecy/ui-v3/ui-compat';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { validateEntityName } from '@prophecy/utils/validation';
import { useQueryClient } from '@tanstack/react-query';
import { useRestMutation } from 'frontend/core/src/data/util';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { addGem, deleteAirflowGem, deleteGem } from '../../../../data/apis/api';
import { AddGemDialog } from './AddGemDialog';
import { airflowColumns, columns } from './columns';
import { GemListToolbar } from './GemlListToolbar';
import { GemTable } from './GemTable';
import { useGemsandLanguages } from './hooks';
import { ComponentListState, GemSectionCategory } from './types';

type ComponentListKeys = keyof ComponentListType;
const defaultListKey = 'gems' as ComponentListKeys;
const categoryToListKey: { [key in UISpecCategory]?: ComponentListKeys } = {
  [UISpecCategory.SourceTarget]: 'formats' as ComponentListKeys
};

export default function ComponentList() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useRestMutation<{ description: string }, Parameters<typeof addGem>[0]>(addGem, {
    sentryTag: `add.gem`
  });
  const globalConf = useGlobalVariables();
  const client = useQueryClient();

  let [, setSearchParams] = useSearchParams();
  const [gemSection, updategemSection] = useState<GemSectionCategory>(GemSectionCategory.Spark);
  const isAirflow = gemSection === GemSectionCategory.Airflow;

  const { language, gemType } = useGemsandLanguages(isAirflow);

  const gemListQuery = useGemList({ enabled: !isAirflow });
  const airflowGemListQuery = useAirflowGemList({ enabled: isAirflow });

  const [{ data }, updateData] = useState<ComponentListState>({
    data: { gems: [], formats: [] }
  });

  const [airflowData, updateAirflowData] = useState<GemRecord[]>([]);
  // find all the uniqye categories in the gem list
  const gemCategories = Object.fromEntries(
    (isAirflow ? airflowData.map((e) => e.category) : data.gems.map((e) => e.category)).map((e) => [e, true])
  );

  useEffect(() => {
    if (gemListQuery.data) {
      const gems = gemListQuery.data.gems;
      const formats = gemListQuery.data.formats;
      updateData((state) => ({ ...state, data: { gems, formats } }));
    }
  }, [gemListQuery.data]);

  useEffect(() => {
    if (airflowGemListQuery.data) {
      updateAirflowData(airflowGemListQuery.data);
    }
  }, [airflowGemListQuery.data]);

  useEffect(() => {
    //reset search params
    setSearchParams('');
  }, [gemSection, setSearchParams]);

  const [showModal, toggleModal] = useState<boolean>(false);

  const handleSubmitCreateGem = useCallback(
    async ({ name, category, language, mode }: GemRecord) => {
      const validationError = validateEntityName(name);
      if (validationError) {
        toast.error({ content: validationError });
        return;
      }

      const listKey = categoryToListKey[category as UISpecCategory] || defaultListKey;
      const list = isAirflow ? airflowData : data[listKey];
      const isDuplicateGem = list.some((item) => item.name === name && item.language === language);
      if (isDuplicateGem) {
        toast.error({ content: name + ' name already exists.' });
        return;
      }

      if (isAirflow) {
        updateAirflowData([{ id: nanoid(), name, category: category, language: language }, ...airflowData]);
      } else {
        updateData({
          data: {
            ...data,
            [listKey]: [{ id: nanoid(), name, category: category, language: language, mode: mode }, ...list]
          }
        });
        mutateAsync(
          { name, category: category, language: language as SpecLanguage, mode: mode },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([QueryKeys.componentList]);
            }
          }
        );
      }

      toggleModal(false);
    },
    [airflowData, data, isAirflow, mutateAsync, queryClient]
  );

  const removeGem = useCallback(
    async (record: GemRecord) => {
      const { name, language, mode, category } = record;

      const dialogResult = await Dialog.alert({
        'data-test-id': 'delete-gem-alert-dialog',
        title: `Are you sure want to delete?`,
        subTitle: 'Deleting this gem will impact all the project using it.',
        children: null,
        iconPlacement: 'center',
        okButton: { children: 'OK', block: false }
      });

      if (dialogResult !== Dialog.ResponseAction.okButton) return;
      try {
        const payload = { name, language, mode, category };
        if (isAirflow) {
          await deleteAirflowGem(payload);
        } else {
          await deleteGem(payload);
        }
      } catch (error) {
        // ignore error for now
        return;
      } finally {
        client.invalidateQueries([QueryKeys.componentList]);
      }
    },
    [client, isAirflow]
  );

  const deleteGemColumn: ColumnType<GemRecord> = useMemo(
    () => ({
      width: '90px',
      render: (_: string, record) => {
        const isProphecyProvided = record.source === 'ProphecyProvided';
        return (
          <TableActions align='end' gap={theme.spaces.x8}>
            <Tooltip title={isProphecyProvided ? 'Prophecy provided gems are not allowed to delete.' : null}>
              <TableIconButton
                onClick={() => removeGem(record as GemRecord)}
                icon={<TrashCIcon type='default' size='xs' />}
                disabled={isProphecyProvided}
                data-test-id='gem-list-delete-gem-button'
                aria-label={`Delete ${record.name} gem`}
              />
            </Tooltip>
          </TableActions>
        );
      }
    }),
    [removeGem]
  );

  const gemTableProps = useMemo(() => {
    const _columns = isAirflow ? airflowColumns : columns;
    const columnsWithDelete = globalConf.isGemDeleteAllowed ? [..._columns, deleteGemColumn] : _columns;
    return {
      columns: columnsWithDelete,
      isLoading: isAirflow ? airflowGemListQuery.isInitialLoading : gemListQuery.isInitialLoading,
      language,
      gemType,
      data,
      airflowData: isAirflow ? airflowData : [],
      isAirflow
    };
  }, [
    isAirflow,
    airflowGemListQuery.isInitialLoading,
    gemListQuery.isInitialLoading,
    language,
    gemType,
    data,
    airflowData,
    globalConf,
    deleteGemColumn
  ]);

  if (gemListQuery.isError) {
    return <span>Error: {gemListQuery.error?.message}</span>;
  }

  return (
    <>
      <DocumentTitle title='Gems' />

      <Stack gap={theme.spaces.x20} padding={theme.spaces.x24} height='100%'>
        <Stack gap={theme.spaces.x24} direction='horizontal' alignY='center' align='space-between'>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x12}>
            <Text level='xl'>Gem List</Text>

            <ToggleGroups
              options={[
                { label: GemSectionCategory.Spark, value: GemSectionCategory.Spark },
                { label: GemSectionCategory.Airflow, value: GemSectionCategory.Airflow }
              ]}
              value={gemSection}
              onChange={(value) => updategemSection(value as GemSectionCategory)}
            />
          </Stack>

          <GemListToolbar section={gemSection} gemCategories={gemCategories} onAddGem={() => toggleModal(true)} />
        </Stack>
        <StackItem grow='1'>
          <GemTable {...gemTableProps} />
        </StackItem>
      </Stack>

      <AddGemDialog
        isAirflow={isAirflow}
        open={showModal}
        onClose={() => toggleModal(false)}
        onAddNewGem={handleSubmitCreateGem}
        updateData={updateData}
      />
    </>
  );
}
